import {Card, Flex, Skeleton, Table, Title, useComputedColorScheme} from '@mantine/core'
import {SubscribeButton} from './components/SubscribeButton.tsx'
import {SubscriptionBadge} from './components/SubscriptionBadge.tsx'
import {useStripe} from '../../providers/stripe/useStripe.ts'
import {FeatureValue} from './components/FeatureValue.tsx'

type Props = {
  disabled: boolean
}

export const SubscriptionsTable = ({disabled: containerDisabled}: Props) => {
  const {subscriptions, loadingSubscriptions} = useStripe()
  const colorScheme = useComputedColorScheme()

  const getSubscriptionColor = (index: number) => {
    if (index === 0) return colorScheme === 'dark' ? 'cyan' : 'cyan.2'
    if (index === 1) return colorScheme === 'dark' ? 'teal' : 'teal.4'
    if (index === 2) return colorScheme === 'dark' ? 'green' : 'green'
  }

  const disabled = containerDisabled

  if (loadingSubscriptions) return <Skeleton maw={800} height={490} />

  return (
    <Card p={0}>
      <Table withColumnBorders={true} withRowBorders={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th h={60} style={{verticalAlign: 'bottom'}} />
            {subscriptions?.map((subscription, i) => (
              <Table.Th key={subscription.id} miw={200} style={{textAlign: 'center'}} bg={getSubscriptionColor(i)}>
                <Title order={4}>{subscription?.name}</Title>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Limits</Table.Th>
            {subscriptions?.map((subscription) => (
              <Table.Th key={subscription.id} />
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td>Active projects</Table.Td>
            {subscriptions?.map((subscription) => (
              <Table.Td key={subscription.id} style={{textAlign: 'center'}}>
                <FeatureValue type={'number'} value={subscription?.features.maxActiveProjects} />
              </Table.Td>
            ))}
          </Table.Tr>
          <Table.Tr>
            <Table.Td>Connections</Table.Td>
            {subscriptions?.map((subscription) => (
              <Table.Td key={subscription.id} style={{textAlign: 'center'}}>
                <FeatureValue type={'number'} value={subscription?.features.maxActiveConnections} />
              </Table.Td>
            ))}
          </Table.Tr>
        </Table.Tbody>

        <Table.Thead>
          <Table.Tr>
            <Table.Th>Time entries</Table.Th>
            {subscriptions?.map((subscription) => (
              <Table.Th key={subscription.id} />
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td>Manual creation</Table.Td>
            {subscriptions?.map((subscription) => (
              <Table.Td key={subscription.id} style={{textAlign: 'center'}}>
                <FeatureValue type={'boolean'} value={subscription.features.manualTimeEntryCreation} />
              </Table.Td>
            ))}
          </Table.Tr>
        </Table.Tbody>

        <Table.Thead>
          <Table.Tr>
            <Table.Th>Recommendations</Table.Th>
            {subscriptions?.map((subscription) => (
              <Table.Th key={subscription.id} />
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td>Automatic refresh</Table.Td>
            {subscriptions?.map((subscription) => (
              <Table.Td key={subscription.id} style={{textAlign: 'center'}}>
                <FeatureValue type={'interval'} value={subscription.features.recommendationRefresh.automaticInterval} />
              </Table.Td>
            ))}
          </Table.Tr>
          <Table.Tr>
            <Table.Td>Manual refresh</Table.Td>
            {subscriptions?.map((subscription) => (
              <Table.Td key={subscription.id} style={{textAlign: 'center', verticalAlign: 'middle'}}>
                <FeatureValue type={'boolean'} value={subscription.features.recommendationRefresh.allowManual} />
              </Table.Td>
            ))}
          </Table.Tr>
        </Table.Tbody>
        <Table.Tfoot>
          <Table.Tr>
            <Table.Td />
            {subscriptions?.map((subscription, i) => (
              <Table.Td key={subscription.id} style={{textAlign: 'center'}}>
                {subscription?.prices
                  .filter((price) => price.interval === 'month')
                  .map((price) => (
                    <SubscribeButton
                      key={price.id}
                      price={price}
                      color={getSubscriptionColor(i)}
                      variant={'filled'}
                      disabled={disabled}
                    />
                  ))}
              </Table.Td>
            ))}
          </Table.Tr>

          <Table.Tr>
            <Table.Td />
            {subscriptions?.map((subscription, i) => (
              <Table.Td key={subscription.id} style={{textAlign: 'center'}}>
                {subscription?.prices
                  .filter((price) => price.interval === 'year')
                  .map((price) => (
                    <SubscribeButton
                      key={price.id}
                      price={price}
                      color={getSubscriptionColor(i)}
                      variant={'filled'}
                      disabled={disabled}
                    />
                  ))}
              </Table.Td>
            ))}
          </Table.Tr>
          {subscriptions?.find((subscription) => subscription.isSubscribed) && (
            <Table.Tr>
              <Table.Th />
              {subscriptions?.map((subscription) => (
                <Table.Th key={subscription.id}>
                  <Flex justify={'center'} gap={5} style={{textAlign: 'center'}}>
                    <SubscriptionBadge subscription={subscription} />
                  </Flex>
                </Table.Th>
              ))}
            </Table.Tr>
          )}
        </Table.Tfoot>
      </Table>
    </Card>
  )
}
